import { useState, useEffect, createContext, useContext } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is already logged in on app start
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      // Set default authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Verify token is still valid
      verifyToken();
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async () => {
    try {
      const response = await axios.get('/auth/verify');
      setUser(response.data.user);
      setLoading(false);
    } catch (error) {
      // Token is invalid, remove it
      localStorage.removeItem('authToken');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
      setLoading(false);
    }
  };

  const login = async (credentials) => {
    try {
      setError(null);
      setLoading(true);
      
      const response = await axios.post('/auth/login', credentials);
      const { 
        token, 
        username, 
        user_type, 
        user_type_name, 
        features, 
        compliance, 
        mfa_required,
        organization 
      } = response.data;
      
      // Store token
      localStorage.setItem('authToken', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Set user with type information
      setUser({ 
        username, 
        user_type, 
        user_type_name, 
        features, 
        compliance, 
        mfa_required,
        organization 
      });
      setLoading(false);
      
      return { success: true };
    } catch (error) {
      setError(error.response?.data?.message || 'Login failed');
      setLoading(false);
      return { success: false, error: error.response?.data?.message || 'Login failed' };
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setError(null);
  };

  const register = async (userData) => {
    try {
      setError(null);
      setLoading(true);
      
      const response = await axios.post('/auth/register', userData);
      const { token, user } = response.data;
      
      // Store token
      localStorage.setItem('authToken', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Set user
      setUser(user);
      setLoading(false);
      
      return { success: true };
    } catch (error) {
      setError(error.response?.data?.message || 'Registration failed');
      setLoading(false);
      return { success: false, error: error.response?.data?.message || 'Registration failed' };
    }
  };

  const updateProfile = async (profileData) => {
    try {
      setError(null);
      
      const response = await axios.put('/settings/profile', profileData);
      setUser(response.data.user);
      
      return { success: true };
    } catch (error) {
      setError(error.response?.data?.message || 'Profile update failed');
      return { success: false, error: error.response?.data?.message || 'Profile update failed' };
    }
  };

  const changePassword = async (passwordData) => {
    try {
      setError(null);
      
      await axios.put('/auth/change-password', passwordData);
      
      return { success: true };
    } catch (error) {
      setError(error.response?.data?.message || 'Password change failed');
      return { success: false, error: error.response?.data?.message || 'Password change failed' };
    }
  };

  const isAuthenticated = !!user;

  const value = {
    user,
    loading,
    error,
    isAuthenticated,
    login,
    logout,
    register,
    updateProfile,
    changePassword,
    clearError: () => setError(null)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
